import { useEffect, useRef } from 'react';
import { acquireModalLock } from '../../utils/modalLock';
import styles from './ConfirmDialog.module.css';

function ConfirmDialog({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel }) {
  const cancelRef = useRef(null);

  // Covers the page, so it owns the keyboard while open — App's global
  // shortcuts bail out on this flag.
  useEffect(() => acquireModalLock(), []);

  // Restores focus to whatever triggered the dialog (e.g. the Reset button)
  // once it closes, so keyboard/screen-reader users aren't dropped onto
  // document.body. Guarded against the trigger having been removed from the
  // DOM in the meantime (e.g. the panel closing along with the confirm).
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    return () => {
      if (previouslyFocused instanceof HTMLElement && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    // Destructive confirmation: default focus goes to Cancel, not the
    // danger-styled confirm button, so a stray Enter/Space right after the
    // dialog opens can't confirm the irreversible action by accident.
    if (cancelRef.current) cancelRef.current.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 id="confirm-title" className={styles.title}>{title}</h2>
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          <button type="button" ref={cancelRef} className={styles.cancel} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className={styles.confirm} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
